import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
    CreateModelDocument,
    type CreateModelMutation,
    type CreateModelMutationVariables,
} from '../../../../api/mutations/createModel';
import { type LocalModelSettings } from '../../../../api/types';
import useLoadingButton from '../../../../components/button/useLoadingButton';
import Form from '../../../../components/fields/Form';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../../utilities/useHandleError';
import AddModelDetails from './AddModelDetails';
import useModelValidator from './useModelValidator';

type FormValues = LocalModelSettings;

const initialValues: FormValues = {
    identifier: '',
    makeId: '',
    name: { defaultValue: '', overrides: [] },
    order: null,
    isActive: true,
    bodyType: null,
};

const AddModel = () => {
    const { t } = useTranslation(['model']);
    const navigate = useNavigate();
    const { setLoading, LoadingButton } = useLoadingButton();
    const validator = useModelValidator();
    const apolloClient = useApolloClient();

    const onSubmit = useHandleError<FormValues>(
        async values => {
            setLoading(true);
            message.loading({
                content: t('model:messages.creationSubmitting'),
                duration: 0,
                key: 'primary',
            });

            // call mutation to create new model
            await apolloClient
                .mutate<CreateModelMutation, CreateModelMutationVariables>({
                    mutation: CreateModelDocument,
                    variables: { settings: values },
                })
                .finally(() => {
                    // clean up
                    message.destroy('primary');
                    setLoading(false);
                });

            // show successful message
            message.success({
                content: t('model:messages.creationSuccessful'),
                key: 'primary',
            });
            navigate('/admin/vehicles/models');
        },
        [setLoading, t, apolloClient, navigate]
    );

    return (
        <ConsolePageWithHeader
            footer={[
                <LoadingButton key="submit" form="addModelForm" htmlType="submit" type="primary">
                    {t('model:actions.create')}
                </LoadingButton>,
            ]}
            onBack={() => navigate('/admin/vehicles/models')}
            title={t('model:addTitle')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validator}>
                {({ handleSubmit }) => (
                    <Form id="addModelForm" name="addModelForm" onSubmitCapture={handleSubmit}>
                        <AddModelDetails />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default AddModel;
